import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import GoogleTranslate from "../../GoogleTranslate";

import { useCookies } from "react-cookie";
import Select from "react-select";
import { fetchConfigs, getLanguage, getLanguageCheck, getTheme } from "../../util/axiosConfig/api/api";
import "./layout.css";
import "./accessibility.css";

// Helper functions to reduce cognitive complexity
const getHostnameInfo = () => {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  return { hostname, isLocalhost };
};

const getDomainVariations = (hostname, isLocalhost) => {
  if (isLocalhost) {
    return ['localhost', '127.0.0.1'];
  }
  return [
    hostname,
    `.${hostname}`,
    hostname.split('.').slice(-2).join('.'),
    `.${hostname.split('.').slice(-2).join('.')}`
  ];
};

const clearCookies = (domains, paths, isLocalhost) => {
  const expires = new Date(0).toUTCString();
  domains.forEach(domain => {
    paths.forEach(path => {
      if (isLocalhost) {
        document.cookie = `googtrans=; expires=${expires}; path=${path}`;
        document.cookie = `googtrans=; expires=${expires}; path=${path}; secure`;
      } else {
        document.cookie = `googtrans=; expires=${expires}; path=${path}; domain=${domain}`;
        document.cookie = `googtrans=; expires=${expires}; path=${path}; domain=${domain}; secure`;
      }
    });
  });
};

const setEnglishCookie = (isLocalhost, setCookie) => {
  if (isLocalhost) {
    document.cookie = `googtrans=%2Fen%2Fen; path=/`;
  } else {
    setCookie("googtrans", "%2Fen%2Fen", {
      path: "/",
      sameSite: "lax",
      secure: true
    });
    document.cookie = `googtrans=%2Fen%2Fen; path=/`;
  }
};

const Header = () => {
  const [cookies, setCookie] = useCookies(["googtrans"]);
  const [logo, setlogo] = useState("");
  const [options, setOptions] = useState([]);
  const [lang, setLang] = useState({});
  const [gLang] = useState("");
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const accessibilityRef = useRef(null);
  const [isLanguageEnabled, setIsLanguageEnabled] = useState(false);
  const [configSettings, setConfigSettings] = useState('');
  const languageFetchRef = useRef(false);
  const lastCookieRef = useRef(null);

  // Fetching the config settings and storing
  const getConfigs = async () => {
    const response = await fetchConfigs();
    console.log("Response fetched : ", response);
    sessionStorage.setItem("Configs", JSON.stringify(response));
    setConfigSettings((JSON.parse(sessionStorage.getItem("Configs"))));
  }

  const toggleAccessibility = () => {
    setAccessibilityOpen((prev) => !prev);
  };

  useEffect(() => {
    checkLanguageEnabled();
    getConfigs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkLanguageEnabled = useCallback(async () => {
    try {
      const response = await getLanguageCheck();
      setIsLanguageEnabled(response.data); 
    } catch (error) {
      console.error('Error checking language status:', error);
      setIsLanguageEnabled(false);
    }
  }, []);

  const handleClickOutside = useCallback((event) => {
    if (
      accessibilityRef.current &&
      !accessibilityRef.current.contains(event.target)
    ) {
      setAccessibilityOpen(false);
    }
  }, []);

  // Helper function to set CSS color properties
  const setCSSColorProperties = useCallback((root, colours) => {
    const defaultColors = {
      primary_color: "27, 96, 152",
      footer_bg_color: "27, 96, 152",
      footer_color: "255, 255, 255",
      header_color: "255, 255, 255",
      btn_bg_color: "27, 96, 152",
      btn_text_color: "255, 255, 255"
    };

    const colorMap = {
      primary_color: "--primary-color",
      footer_bg_color: "--footer-bg-color",
      footer_color: "--footer-color",
      header_color: "--header-color",
      btn_bg_color: "--btn-bg-color",
      btn_text_color: "--btn-text-color"
    };

    Object.keys(colorMap).forEach(colorKey => {
      root.style.setProperty(
        colorMap[colorKey],
        colours[colorKey] === undefined ? defaultColors[colorKey] : colours[colorKey]
      );
    });
  }, []);

  // Helper function to setup favicon
  const setupFavicon = useCallback((titleLogo) => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }
    link.href = titleLogo;
  }, []);

  const getUserThemelog = useCallback(() => {
    const response = getTheme();

    response.then(
      (success) => {
        setlogo(success.logo);
        document.title = success.title;
        localStorage.setItem("shortLogo", success.shortLogo);
        setupFavicon(success.titleLogo);
        
        if (success.colours) {
          console.log(success.colours.primary_color, "colours console");
          const root = document.documentElement;
          setCSSColorProperties(root, success.colours);
        }
      },
      (error) => {
        console.error("Error fetching theme:", error);
      }
    );
  }, [setupFavicon, setCSSColorProperties]);
  
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    getUserThemelog();
  }, [getUserThemelog]);

  const changeLanguage = (value) => {
    if (value === null) {
      setLang({});
      return;
    }

    localStorage.setItem("supportedWGLanguage", value.value);
    setLang({ label: value.localLanguage, value: value.value });

    const cookieDomain = window.location.hostname.split('.').slice(-2).join('.');
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${cookieDomain}`;
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${cookieDomain}`;

    const cookieValue = value.value.toLowerCase() === 'en' 
      ? '%2Fen%2Fen' 
      : `/en/${value.value.toLowerCase()}`;

    setCookie("googtrans", cookieValue, { path: "/" });
    window.location.reload();
  };

  // Helper function to handle single English language
  const handleSingleEnglishLanguage = useCallback((enabledLanguages) => {
    if (enabledLanguages.length !== 1 || enabledLanguages[0].id.toLowerCase() !== 'en') {
      return false;
    }

    setLang({ label: 'English', value: 'en' });
    setOptions([{ value: 'en', label: 'English' }]);
    localStorage.setItem("supportedWGLanguage", 'en');
    
    const { hostname, isLocalhost } = getHostnameInfo();
    const currentCookie = document.cookie
      .split(';')
      .find(c => c.trim().startsWith('googtrans='));
      
    if (currentCookie && currentCookie.trim() !== 'googtrans=%2Fen%2Fen') {
      const domains = getDomainVariations(hostname, isLocalhost);
      const paths = ['/', '/translate'];
      
      clearCookies(domains, paths, isLocalhost);
      
      try {
        setEnglishCookie(isLocalhost, setCookie);
        window.location.reload();
      } catch (error) {
        console.error('Error setting cookie:', error);
        window.location.reload();
      }
    }
    
    return true;
  }, [setCookie]);

  // Helper function to set language cookie
  const setLanguageCookie = useCallback((item, cookieDomain) => {
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${cookieDomain}`;
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${cookieDomain}`;

    const cookieValue = item.id.toLowerCase() === 'en' 
      ? '%2Fen%2Fen' 
      : `/en/${item.id.toLowerCase()}`;

    setCookie("googtrans", cookieValue, {
      path: "/",
      domain: `.${cookieDomain}`,
      sameSite: "lax"
    });

    localStorage.setItem("supportedWGLanguage", item.id);
    setLang({ label: item.localLanguage, value: item.id });
  }, [setCookie]);

  // Helper function to process enabled languages
  const processEnabledLanguages = useCallback((enabledLanguages, lastPart) => {
    const newSupportedLanguages = [];
    let languageFound = false;

    enabledLanguages.forEach((item) => {
      newSupportedLanguages.push(item.id);

      if (lastPart.toLowerCase() === item.id.toLowerCase()) {
        languageFound = true;
        const cookieDomain = window.location.hostname.split('.').slice(-2).join('.');
        setLanguageCookie(item, cookieDomain);
      }

      setOptions(prevData => [...prevData, { 
        value: item.id, 
        label: item.localLanguage 
      }]);
    });

    return { newSupportedLanguages, languageFound };
  }, [setLanguageCookie]);

  // Helper function to handle fallback to English
  const handleFallbackToEnglish = useCallback((enabledLanguages) => {
    const englishLang = enabledLanguages.find(item => item.id.toLowerCase() === 'en');
    
    if (!englishLang) return;

    const { hostname, isLocalhost } = getHostnameInfo();
    const currentCookie = document.cookie
      .split(';')
      .find(c => c.trim().startsWith('googtrans='));

    if (currentCookie && currentCookie.trim() !== 'googtrans=%2Fen%2Fen') {
      const domains = getDomainVariations(hostname, isLocalhost);
      const paths = ['/', '/translate'];
      
      clearCookies(domains, paths, isLocalhost);

      try {
        setEnglishCookie(isLocalhost, setCookie);
        localStorage.setItem("supportedWGLanguage", 'en');
        setLang({ label: englishLang.localLanguage, value: englishLang.id });
        window.location.reload();
      } catch (error) {
        console.error('Error setting cookie:', error);
        window.location.reload();
      }
    } else {
      localStorage.setItem("supportedWGLanguage", 'en');
      setLang({ label: englishLang.localLanguage, value: englishLang.id });
    }
  }, [setCookie]);

  const fetchLanguage = useCallback((lastPart) => {
    const resp = getLanguage();
    resp.then(
      (success) => {
        setOptions([]);
        const enabledLanguages = success.filter(item => item.enable === true);

        if (handleSingleEnglishLanguage(enabledLanguages)) {
          return;
        }

        const { newSupportedLanguages, languageFound } = processEnabledLanguages(enabledLanguages, lastPart);

        if (!languageFound) {
          handleFallbackToEnglish(enabledLanguages);
        }

        localStorage.setItem("supportedWTLanguage", newSupportedLanguages.join(','));
      },
      (error) => {
        console.error("Error fetching languages:", error);
      }
    );
  }, [handleSingleEnglishLanguage, processEnabledLanguages, handleFallbackToEnglish]);

  useEffect(() => {
    const savedTranslation = cookies.googtrans;
    const cookieKey = savedTranslation || 'default';
    
    // Prevent fetching if already in progress or same cookie value
    if (languageFetchRef.current || lastCookieRef.current === cookieKey) {
      return;
    }
    
    languageFetchRef.current = true;
    lastCookieRef.current = cookieKey;
    
    if (savedTranslation) {
      let lang;
      
      if (savedTranslation === '%2Fen%2Fen') {
        lang = 'en';
      } else {
        lang = savedTranslation.split('/').pop();
      }
      
      console.log("Before setting Lang: ", lang);
      localStorage.setItem("Glang", lang);
      console.log("After setting Lang: ", lang);
      fetchLanguage(lang);
    } else {
      const defaultLang = localStorage.getItem("supportedWGLanguage") || "en";
      fetchLanguage(defaultLang);
    }
    
    // Reset the fetch flag after a short delay
    setTimeout(() => {
      languageFetchRef.current = false;
    }, 100);
  }, [gLang, cookies.googtrans, fetchLanguage]);

  const [activeAccessibility, setActiveAccessibility] = useState(() => {
    const savedSettings = localStorage.getItem("accessibilitySettings");
    return savedSettings
      ? JSON.parse(savedSettings)
      : {
          contrast: false,
          spacing: false,
          cursor: false,
          lineHeight: false,
          hideImage: false,
          highlightLinks: false,
          saturation: false,
          invert: false,
        };
  });

  const toggleBodyClass = (key, className) => {
    const isActive = activeAccessibility[key];

    if (isActive) {
      document.body.classList.remove(className);
    } else {
      document.body.classList.add(className);
    }

    const newSettings = {
      ...activeAccessibility,
      [key]: !isActive,
    };

    setActiveAccessibility(newSettings);
    localStorage.setItem("accessibilitySettings", JSON.stringify(newSettings));
  };

  useEffect(() => {
    Object.entries(activeAccessibility).forEach(([key, isActive]) => {
      const className = {
        contrast: "contrast",
        spacing: "addLetterSpacing",
        cursor: "big_cursor",
        lineHeight: "line_Height",
        hideImage: "hide_Image",
        highlightLinks: "highlight_Links",
        saturation: "saturation",
        invert: "invert",
      }[key];

      if (isActive && className) {
        document.body.classList.add(className);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fontSizes = [
    "font-12",
    "font-14",
    "font-16",
    "font-18",
    "font-20",
    "font-22",
  ];

  const getInitialFontIndex = () => {
    const savedFont = localStorage.getItem("fontSize");
    return savedFont ? fontSizes.indexOf(savedFont) || 2 : 2;
  };

  const [currentIndex, setCurrentIndex] = useState(getInitialFontIndex);

  useEffect(() => {
    document.body.classList.remove(...fontSizes);
    const selectedFont = fontSizes[currentIndex];
    document.body.classList.add(selectedFont);
    localStorage.setItem("fontSize", selectedFont);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const handleIncrease = () => {
    if (currentIndex < fontSizes.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleDecrease = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };
  
  const handleNormalContrast = () => {
    setCurrentIndex(2);
  };
  
  const customStylesSelect = {
    option: (provided, state) => ({
      ...provided,
      color: "#000", // text color
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#000", // selected value color
    }),
  };

  return (
    <header className="header">
      <div className="container-lg">
        <div className="top_bar">
          <div className="brand_logo">
            <Link to="/">
              {" "}
              <img src={logo} className="brand_logo_img" alt="Brand Logo"></img>
            </Link>
          </div>
            
            <div 
              className="col-xxl-2 col-xl-2 col-lg-2 col-md-2 col-4 col-sm-4 lang"
              style={{ visibility: isLanguageEnabled ? "visible" : "hidden" }}
            >
              <Select
                className="Selectontrol notranslate"
                value={lang}
                onChange={changeLanguage}
                options={options}
                styles={customStylesSelect}
              />
            </div>
        {configSettings.isAcceessbilityFeatureEnabled && (
          <div
            className="accessibility-wrapper"
            ref={accessibilityRef}
            style={{ position: "relative" }}
          >
            <span
              // className="access-box"
              className={`access-box accessibility-icon ${
                accessibilityOpen ? "active" : ""
              }`}
              tabIndex={0}
              onClick={toggleAccessibility}
              // aria-label="Toggle accessibility tools"
            >
              <svg
                viewBox="0 0 33 34"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.4994 0.814209C18.4722 0.814209 20.0863 2.3018 20.0863 4.11998C20.0863 5.93815 18.4722 7.42574 16.4994 7.42574C14.5266 7.42574 12.9125 5.93815 12.9125 4.11998C12.9125 2.3018 14.5266 0.814209 16.4994 0.814209ZM32.6404 12.3844H21.8798V33.8719H18.2929V23.9546H14.706V33.8719H11.1191V12.3844H0.358398V9.07863H32.6404V12.3844Z"
                  fill=""
                ></path>
              </svg>
            </span>

            {accessibilityOpen && (
              <div className="accessibility-dropdown">
                <h5 className="accessibility-heading">Accessibility Tools</h5>
                <div className="acess-item">
                  <h4>Color contrast</h4>
                  <div className="access-item-row">
                    <button
                      className={activeAccessibility.contrast ? "active" : ""}
                      onClick={() => toggleBodyClass("contrast", "contrast")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                      >
                        <path d="M480-288.46 531.54-340H620v-88.46L671.54-480 620-531.54V-620h-88.46L480-671.54 428.46-620H340v88.46L288.46-480 340-428.46V-340h88.46L480-288.46Zm0-81.54v-220q45.77 0 77.88 32.12Q590-525.77 590-480q0 45.77-32.12 77.88Q525.77-370 480-370ZM172.31-180Q142-180 121-201q-21-21-21-51.31v-455.38Q100-738 121-759q21-21 51.31-21h615.38Q818-780 839-759q21 21 21 51.31v455.38Q860-222 839-201q-21 21-51.31 21H172.31Zm0-60h615.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-455.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H172.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v455.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85ZM160-240v-480 480Z"></path>
                      </svg>
                      <span>High Contrast</span>
                    </button>
                    <button
                      className={activeAccessibility.contrast === false ? "active" : ""}
                      onClick={() => toggleBodyClass("contrast", "contrast")}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M12 16.7885L13.2885 15.5H15.5V13.2885L16.7885 12L15.5 10.7115V8.5H13.2885L12 7.2115L10.7115 8.5H8.5V10.7115L7.2115 12L8.5 13.2885V15.5H10.7115L12 16.7885ZM4.30775 19.5C3.80258 19.5 3.375 19.325 3.025 18.975C2.675 18.625 2.5 18.1974 2.5 17.6923V6.30775C2.5 5.80258 2.675 5.375 3.025 5.025C3.375 4.675 3.80258 4.5 4.30775 4.5H19.6923C20.1974 4.5 20.625 4.675 20.975 5.025C21.325 5.375 21.5 5.80258 21.5 6.30775V17.6923C21.5 18.1974 21.325 18.625 20.975 18.975C20.625 19.325 20.1974 19.5 19.6923 19.5H4.30775ZM4.30775 18H19.6923C19.7693 18 19.8398 17.9679 19.9038 17.9038C19.9679 17.8398 20 17.7693 20 17.6923V6.30775C20 6.23075 19.9679 6.16025 19.9038 6.09625C19.8398 6.03208 19.7693 6 19.6923 6H4.30775C4.23075 6 4.16025 6.03208 4.09625 6.09625C4.03208 6.16025 4 6.23075 4 6.30775V17.6923C4 17.7693 4.03208 17.8398 4.09625 17.9038C4.16025 17.9679 4.23075 18 4.30775 18Z"></path>
                        <path
                          d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z"
                          fill="white"
                        ></path>
                      </svg>
                      <span>Normal Contrast</span>
                    </button>

                    <button
                      className={
                        activeAccessibility.highlightLinks ? "active" : ""
                      }
                      onClick={() =>
                        toggleBodyClass("highlightLinks", "highlight_Links")
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                      >
                        <path d="M432.31-298.46H281.54q-75.34 0-128.44-53.1Q100-404.65 100-479.98q0-75.33 53.1-128.44 53.1-53.12 128.44-53.12h150.77v60H281.54q-50.39 0-85.96 35.58Q160-530.38 160-480q0 50.38 35.58 85.96 35.57 35.58 85.96 35.58h150.77v60ZM330-450v-60h300v60H330Zm197.69 151.54v-60h150.77q50.39 0 85.96-35.58Q800-429.62 800-480q0-50.38-35.58-85.96-35.57-35.58-85.96-35.58H527.69v-60h150.77q75.34 0 128.44 53.1Q860-555.35 860-480.02q0 75.33-53.1 128.44-53.1 53.12-128.44 53.12H527.69Z"></path>
                      </svg>
                      <span>Highlight links</span>
                    </button>
                    <button
                      className={activeAccessibility.saturation ? "active" : ""}
                      onClick={() =>
                        toggleBodyClass("saturation", "saturation")
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                      >
                        <path d="M480-140q-124.92 0-212.46-86.35Q180-312.69 180-435.62q0-62.53 23.66-114.69 23.65-52.15 64.19-93.84L480-852.31l212.15 208.16q40.54 41.69 64.19 94.42Q780-497 780-435.62q0 122.93-87.54 209.27Q604.92-140 480-140Zm0-60v-568L310-600q-35 33-52.5 74.69T240-435.62q0 97 70 166.31T480-200Z"></path>
                      </svg>
                      <span>Saturation</span>
                    </button>
                    <button
                      className={activeAccessibility.invert ? "active" : ""}
                      onClick={() => toggleBodyClass("invert", "invert")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                      >
                        <path d="M480.07-100q-78.84 0-148.21-29.92t-120.68-81.21q-51.31-51.29-81.25-120.63Q100-401.1 100-479.93q0-78.84 29.92-148.21t81.21-120.68q51.29-51.31 120.63-81.25Q401.1-860 479.93-860q78.84 0 148.21 29.92t120.68 81.21q51.31 51.29 81.25 120.63Q860-558.9 860-480.07q0 78.84-29.92 148.21t-81.21 120.68q-51.29 51.31-120.63 81.25Q558.9-100 480.07-100ZM510-161.85q121.31-11.53 205.65-101.42Q800-353.15 800-480t-83.96-216.35q-83.96-89.5-206.04-101.8v636.3Z"></path>
                      </svg>
                      <span>Invert</span>
                    </button>
                  </div>
                </div>
                <div className="acess-item">
                  <h4>Text Size</h4>
                  <div className="access-item-row">
                    <button onClick={handleIncrease}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                      >
                        <path d="m52.31-210 210.77-540h69.23l210.77 540h-72.16l-54.46-144.15H177.38L122.92-210H52.31Zm146.77-204h195.69l-95.2-252h-4.65l-95.84 252Zm528.61 84v-120h-120v-60h120v-120h60v120h120v60h-120v120h-60Z" />
                      </svg>
                      <span>Font Size Increase</span>
                    </button>
                    <button onClick={handleDecrease}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                      >
                        <path d="m52.31-210 210.77-540h69.23l210.77 540h-72.16l-54.46-144.15H177.38L122.92-210H52.31Zm146.77-204h195.69l-95.08-252h-4.77l-95.84 252Zm408.61-36v-60h300v60h-300Z" />
                      </svg>
                      <span>Font Size Decrease</span>
                    </button>{" "}
                    <button onClick={handleNormalContrast}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M6 18.75L11.2692 5.25H13L18.2693 18.75H16.4653L15.1038 15.1463H9.12675L7.76525 18.75H6ZM9.66925 13.65H14.5615L12.1845 7.35H12.0653L9.66925 13.65Z"></path>
                      </svg>
                      <span>Normal Font</span>
                    </button>
                    <button
                      className={activeAccessibility.spacing ? "active" : ""}
                      onClick={() =>
                        toggleBodyClass("spacing", "addLetterSpacing")
                      }
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M16.5852 5H8.15341C8.06903 5 8 5.06903 8 5.15341V7.60795C8 7.69233 8.06903 7.76136 8.15341 7.76136H9.22727C9.31165 7.76136 9.38068 7.69233 9.38068 7.60795V6.38068H11.6023V13.1193H9.83807C9.75369 13.1193 9.68466 13.1884 9.68466 13.2727V14.3466C9.68466 14.431 9.75369 14.5 9.83807 14.5H14.9006C14.9849 14.5 15.054 14.431 15.054 14.3466V13.2727C15.054 13.1884 14.9849 13.1193 14.9006 13.1193H13.1364V6.38068H15.358V7.60795C15.358 7.69233 15.427 7.76136 15.5114 7.76136H16.5852C16.6696 7.76136 16.7386 7.69233 16.7386 7.60795V5.15341C16.7386 5.06903 16.6696 5 16.5852 5Z"></path>
                        <path d="M16.1648 14.9953V16.2379H8.72444V14.9953C8.72444 14.8802 8.59021 14.815 8.50008 14.886L6.05321 16.819C6.03665 16.8317 6.02324 16.8482 6.01402 16.8669C6.0048 16.8857 6 16.9064 6 16.9273C6 16.9482 6.0048 16.9689 6.01402 16.9876C6.02324 17.0064 6.03665 17.0228 6.05321 17.0356L8.50008 18.9686C8.52055 18.9847 8.54515 18.9947 8.57105 18.9975C8.59695 19.0003 8.6231 18.9957 8.64652 18.9843C8.66994 18.9729 8.68966 18.9551 8.70345 18.933C8.71723 18.9109 8.7245 18.8853 8.72444 18.8593V17.6186H16.1648V18.8612C16.1648 18.9763 16.299 19.0415 16.3891 18.9705L18.8341 17.0376C18.9051 16.9819 18.9051 16.8746 18.8341 16.8209L16.3891 14.8879C16.3689 14.8717 16.3444 14.8615 16.3186 14.8585C16.2928 14.8556 16.2667 14.8599 16.2432 14.8712C16.2198 14.8824 16.2 14.9 16.1861 14.9219C16.1723 14.9439 16.1649 14.9693 16.1648 14.9953Z"></path>
                      </svg>
                      <span>Text Spacing</span>
                    </button>
                    <button
                      className={activeAccessibility.lineHeight ? "active" : ""}
                      onClick={() =>
                        toggleBodyClass("lineHeight", "line_Height")
                      }
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M14.5852 5H4.15341C4.06903 5 4 5.06903 4 5.15341V7.60795C4 7.69233 4.06903 7.76136 4.15341 7.76136H5.22727C5.31165 7.76136 5.38068 7.69233 5.38068 7.60795V6.38068H8.60227V17.1193H6.83807C6.75369 17.1193 6.68466 17.1884 6.68466 17.2727V18.3466C6.68466 18.431 6.75369 18.5 6.83807 18.5H11.9006C11.9849 18.5 12.054 18.431 12.054 18.3466V17.2727C12.054 17.1884 11.9849 17.1193 11.9006 17.1193H10.1364V6.38068H13.358V7.60795C13.358 7.69233 13.427 7.76136 13.5114 7.76136H14.5852C14.6696 7.76136 14.7386 7.69233 14.7386 7.60795V5.15341C14.7386 5.06903 14.6696 5 14.5852 5ZM19.8165 15.4702H18.5739V8.02983H19.8165C19.9315 8.02983 19.9967 7.8956 19.9258 7.80547L17.9928 5.35859C17.98 5.34204 17.9636 5.32863 17.9448 5.31941C17.9261 5.31018 17.9054 5.30539 17.8845 5.30539C17.8636 5.30539 17.8429 5.31018 17.8241 5.31941C17.8053 5.32863 17.7889 5.34204 17.7761 5.35859L15.8432 7.80547C15.8271 7.82594 15.8171 7.85054 15.8143 7.87644C15.8115 7.90234 15.8161 7.92849 15.8275 7.95191C15.8389 7.97532 15.8567 7.99505 15.8788 8.00883C15.9009 8.02262 15.9264 8.02989 15.9525 8.02983H17.1932V15.4702H15.9506C15.8355 15.4702 15.7703 15.6044 15.8413 15.6945L17.7742 18.1395C17.8298 18.2104 17.9372 18.2104 17.9909 18.1395L19.9239 15.6945C19.9401 15.6743 19.9503 15.6498 19.9533 15.624C19.9562 15.5982 19.9518 15.5721 19.9406 15.5486C19.9294 15.5252 19.9118 15.5054 19.8898 15.4915C19.8679 15.4776 19.8425 15.4702 19.8165 15.4702Z"></path>
                      </svg>
                      <span>Line Height</span>
                    </button>
                  </div>
                </div>
                <div className="acess-item">
                  <h4>Others</h4>
                  <div className="access-item-row">
                    <button
                      className={activeAccessibility.hideImage ? "active" : ""}
                      onClick={() => toggleBodyClass("hideImage", "hide_Image")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                      >
                        <path d="m820-254-60-60v-433.69q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H314l-60-60h493.69Q778-820 799-799q21 21 21 51.31V-254ZM805.85-69.85 735.69-140H212.31Q182-140 161-161q-21-21-21-51.31v-523.38l-70.15-70.16L112-848l736 736-42.15 42.15ZM270-290l99.23-131.54 80 102.31L496.08-379 200-675.08v462.77q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h462.77l-90-90H270Zm267-247Zm-99.15 99.15Z"></path>
                      </svg>
                      <span>Hide images</span>
                    </button>
                    <button
                      className={activeAccessibility.cursor ? "active" : ""}
                      onClick={() => toggleBodyClass("cursor", "big_cursor")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                      >
                        <path d="M80-490v-60h120v60H80Zm129.08 224.31-42.16-43.39 84-84 43.39 42.16-85.23 85.23Zm41.84-381.23-84-84 42.16-43.39 85.23 85.23-43.39 42.16ZM699.23-190 515.38-373.85l-43.84 133.08-103.85-344.61 346.15 103.84L582-434.92l181.07 181.07L699.23-190ZM402.31-720v-120h60v120h-60Zm211.38 73.08-43.38-42.16 85.23-85.23 42.15 42.16-84 85.23Z"></path>
                      </svg>
                      <span>Big Cursor</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
          <div className="login_signup">
            <GoogleTranslate />

            <div className="login_signup_btn_area">
              {/* <a
                className="login_btn"
                href={`${ENV.API_KEY_URL_BEN}/beneficiary-app`}
                target="_blank"
                rel="noreferrer"
              >
                {" "}
                Login{" "}
              </a> */}
              {/* <Link to="/createAccount"><button className="signup_btn">Sign Up</button></Link> */}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
